import {
  createRef,
  RefObject,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react'

import { motion, useSpring } from 'framer-motion'
import { Trans } from '@lingui/react/macro'
import { GraphQL } from '@guestgoodie/graphql'
import { Currency } from '@guestgoodie/pricing'

import { ProgressStepper } from '@/components/ui/ProgressStepper'
import Button from '@/components/ui/Button'
import { BillingStep, useSteps } from '../hooks/useSteps'
import { BillingContext, useBillingData } from '../hooks/useBillingData'

import {
  CustomerFormData,
  RoomsFormData,
  useBillingForm
} from '../hooks/useBillingForm'

const ANIMATION_DURATION = 0.3

export interface RefType {
  submitCurrent: () => void
}

function FormTransitions({
  index,
  ref,
  steps,
  currency,
  onStepDone
}: {
  index: number
  ref: RefObject<RefType | null>
  steps: BillingStep[]
  currency: GraphQL.BillingCurrency
  onStepDone: () => void
}) {
  const lastIdx = useRef(index)
  const scrollRef = useRef<HTMLDivElement>(null)
  const spring = useSpring(0, { duration: ANIMATION_DURATION })

  const formRefs = useRef<RefObject<HTMLFormElement | null>[]>(
    steps.map(() => createRef<HTMLFormElement | null>())
  )

  const submitCurrent = useCallback(() => {
    formRefs.current[index]?.current?.requestSubmit()
  }, [index])

  useImperativeHandle(ref, () => {
    return {
      submitCurrent
    }
  }, [submitCurrent])

  useEffect(() => {
    if (!scrollRef.current) return
    if (lastIdx.current === index) return
    lastIdx.current = index

    spring.set(scrollRef.current.clientWidth * index)
  }, [index, spring])

  useEffect(() => {
    const unsubscribe = spring.on('change', left => {
      scrollRef.current?.scrollTo({
        left,
        behavior: 'smooth'
      })
    })

    return () => {
      unsubscribe()
    }
  }, [spring])

  return (
    <div className="w-full overflow-hidden relative my-8" ref={scrollRef}>
      <div className="flex flex-row justify-evenly items-start w-[300%]">
        {steps.map(function (step, idx) {
          const show = idx === index

          return (
            <motion.div
              key={step.value}
              className="flex-1"
              initial={{ height: show ? 'auto' : 0 }}
              animate={{ height: show ? 'auto' : 0 }}
              transition={{ duration: ANIMATION_DURATION }}
              inert={!show}
            >
              <step.Component
                ref={formRefs.current[idx]}
                currency={currency as unknown as Currency}
                onDone={onStepDone}
              />
            </motion.div>
          )
        })}
      </div>
    </div>
  )
}

export function BillingForm({ onDone }: { onDone: () => void }) {
  const { isNew, billing, setBilling } = useBillingData()
  const { step, steps, index: stepIndex, isLast, increaseStep } = useSteps()
  const transitionRef = useRef<RefType>(null)

  const { activate, update, errors, loading, currency } = useBillingForm({
    onDone
  })

  const onStepDone = useCallback(() => {
    if (isLast) {
      onDone()

      return
    }

    increaseStep(1)
  }, [isLast, increaseStep, onDone])

  const value = useMemo(() => {
    return {
      billing,
      isNew,
      serverErrors: errors,
      update(data: CustomerFormData | RoomsFormData) {
        setBilling(data)
        onStepDone()
      },
      save: isNew ? activate : update
    }
  }, [billing, isNew, errors, activate, update, setBilling, onStepDone])

  return (
    <BillingContext value={value}>
      <div className="flex flex-col gap-4">
        <FormTransitions
          ref={transitionRef}
          steps={steps}
          index={stepIndex}
          currency={currency}
          onStepDone={onStepDone}
        />
        <ProgressStepper value={step} steps={steps} />
        <div className="flex flex-row justify-end gap-2.5">
          {stepIndex > 0 && (
            <Button onClick={() => increaseStep(-1)}>
              <Trans>Back</Trans>
            </Button>
          )}
          {stepIndex < steps.length - 1 && (
            <Button onClick={() => transitionRef.current?.submitCurrent()}>
              <Trans>Next</Trans>
            </Button>
          )}
          {stepIndex === steps.length - 1 && (
            <Button
              onClick={() => transitionRef.current?.submitCurrent()}
              loading={loading}
            >
              {isNew ? <Trans>Subscribe</Trans> : <Trans>Update</Trans>}
            </Button>
          )}
        </div>
      </div>
    </BillingContext>
  )
}
